import { useState } from 'react';

import { Mail, Plus, Save, Trash2, UserCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  useHumanReview,
  useManualEmails,
  useSendManualEmail,
  useUpsertHumanReview,
} from '@/hooks/useCalls';
import { useEffectiveOutcomes } from '@/hooks/useOutcomes';
import { SHIFT_TYPE_OPTIONS } from '@/lib/api/reviews.api';
import { useFeaturesStore } from '@/stores/features.store';
import { calculateAvailabilityHours } from '@/utils/availability';
import { formatDateTime } from '@/utils/format';

import { KNOWN_CALL_OUTCOMES } from '@repo/shared-types';

import type { CallWithContact } from '@repo/shared-types';

interface HumanReviewFormProps {
  callId: string;
  call: CallWithContact;
}

export const HumanReviewForm = ({ callId, call }: HumanReviewFormProps) => {
  const { toast } = useToast();
  const humanReviewEnabled = useFeaturesStore((s) => s.humanReview);
  const humanReviewDataField = useFeaturesStore((s) => s.humanReviewDataField);
  const isLeadDataMode = humanReviewDataField === 'lead_data';

  // Dynamic platform-wide outcome vocabulary.
  const { data: outcomeDefinitions } = useEffectiveOutcomes();
  const outcomeOptions =
    outcomeDefinitions && outcomeDefinitions.length > 0
      ? outcomeDefinitions.map((d) => ({ value: d.key, label: d.label }))
      : KNOWN_CALL_OUTCOMES.map((o) => ({ value: o as string, label: o.replace(/_/g, ' ') }));
  const { data: humanReview } = useHumanReview(humanReviewEnabled ? callId : undefined);
  const upsertHumanReviewMutation = useUpsertHumanReview(callId);
  const { data: manualEmails = [] } = useManualEmails(humanReviewEnabled ? callId : undefined);
  const sendManualEmailMutation = useSendManualEmail(callId);

  const [confirmSendOpen, setConfirmSendOpen] = useState(false);

  // Human review form state synced from data during render (avoids useEffect+setState)
  const [hrSyncKey, setHrSyncKey] = useState<string>('');
  const [hrOutcome, setHrOutcome] = useState<string>('');
  const [hrSlots, setHrSlots] = useState<{ date: string; shift_type: string }[]>([]);
  const [hrNotes, setHrNotes] = useState<string>('');
  const [hrConfirmed, setHrConfirmed] = useState<boolean>(false);
  const [hrConsented, setHrConsented] = useState<boolean>(false);
  // Lead data form state
  const [hrCustomerStatus, setHrCustomerStatus] = useState<string>('');
  const [hrCustomerName, setHrCustomerName] = useState<string>('');
  const [hrVehicleInterest, setHrVehicleInterest] = useState<string>('');
  const [hrBudget, setHrBudget] = useState<string>('');
  const [hrFinancePreference, setHrFinancePreference] = useState<string>('');
  const [hrShowroomInterest, setHrShowroomInterest] = useState<boolean>(false);
  const [hrPreferredCallbackTime, setHrPreferredCallbackTime] = useState<string>('');
  const [hrLeadNotes, setHrLeadNotes] = useState<string>('');

  const nextHrSyncKey = `${humanReview?.id ?? ''}|${humanReview?.updated_at ?? ''}|${call?.id ?? ''}`;
  if (nextHrSyncKey !== hrSyncKey) {
    setHrSyncKey(nextHrSyncKey);
    setHrOutcome((humanReview?.outcome ?? call?.outcome) || '');

    if (isLeadDataMode) {
      const leadSource =
        humanReview?.lead_data ?? (call?.lead_data as Record<string, unknown> | null);
      if (leadSource && typeof leadSource === 'object') {
        setHrCustomerStatus((leadSource.customer_status as string) || '');
        setHrCustomerName((leadSource.customer_name as string) || '');
        setHrVehicleInterest((leadSource.vehicle_interest as string) || '');
        setHrBudget((leadSource.budget as string) || '');
        setHrFinancePreference((leadSource.finance_preference as string) || '');
        setHrShowroomInterest(!!leadSource.showroom_interest);
        setHrPreferredCallbackTime((leadSource.preferred_callback_time as string) || '');
        setHrLeadNotes((leadSource.additional_notes as string) || '');
      } else {
        setHrCustomerStatus('');
        setHrCustomerName('');
        setHrVehicleInterest('');
        setHrBudget('');
        setHrFinancePreference('');
        setHrShowroomInterest(false);
        setHrPreferredCallbackTime('');
        setHrLeadNotes('');
      }
    } else {
      const source = humanReview?.availability_data ?? call?.availability_data;
      if (source && typeof source === 'object') {
        const data = source as Record<string, unknown>;
        const slots = Array.isArray(data.availability_slots) ? data.availability_slots : [];
        setHrSlots(
          slots.map((s: any) => ({
            date: s.date || '',
            shift_type: s.shift_type || 'day',
          })),
        );
        setHrNotes((data.additional_notes as string) || '');
        setHrConfirmed(!!data.confirmed);
        setHrConsented(!!data.consented);
      } else {
        setHrSlots([]);
        setHrNotes('');
        setHrConfirmed(false);
        setHrConsented(false);
      }
    }
  }

  const handleSave = () => {
    if (isLeadDataMode) {
      const leadData: Record<string, unknown> = {};
      if (hrCustomerStatus) leadData.customer_status = hrCustomerStatus;
      if (hrCustomerName.trim()) leadData.customer_name = hrCustomerName.trim();
      if (hrVehicleInterest.trim()) leadData.vehicle_interest = hrVehicleInterest.trim();
      if (hrBudget.trim()) leadData.budget = hrBudget.trim();
      if (hrFinancePreference) leadData.finance_preference = hrFinancePreference;
      leadData.showroom_interest = hrShowroomInterest;
      if (hrPreferredCallbackTime.trim())
        leadData.preferred_callback_time = hrPreferredCallbackTime.trim();
      if (hrLeadNotes.trim()) leadData.additional_notes = hrLeadNotes.trim();

      const hasData =
        hrCustomerStatus ||
        hrCustomerName.trim() ||
        hrVehicleInterest.trim() ||
        hrBudget.trim() ||
        hrFinancePreference ||
        hrShowroomInterest ||
        hrPreferredCallbackTime.trim() ||
        hrLeadNotes.trim();

      upsertHumanReviewMutation.mutate(
        {
          outcome: hrOutcome || null,
          lead_data: hasData ? leadData : null,
        },
        {
          onSuccess: () => {
            toast({
              variant: 'success',
              title: 'Human review saved',
              description: 'The human review data has been saved.',
            });
          },
          onError: () => {
            toast({
              variant: 'destructive',
              title: 'Save failed',
              description: 'Failed to save human review data.',
            });
          },
        },
      );
    } else {
      const availabilityData: Record<string, unknown> = {
        availability_slots: hrSlots.filter((s) => s.date),
        confirmed: hrConfirmed,
        consented: hrConsented,
      };
      if (hrNotes.trim()) {
        availabilityData.additional_notes = hrNotes.trim();
      }

      const hasSlots = hrSlots.some((s) => s.date);
      upsertHumanReviewMutation.mutate(
        {
          outcome: hrOutcome || null,
          availability_data:
            hasSlots || hrNotes.trim() || hrConfirmed || hrConsented ? availabilityData : null,
        },
        {
          onSuccess: () => {
            toast({
              variant: 'success',
              title: 'Human review saved',
              description: 'The human review data has been saved.',
            });
          },
          onError: () => {
            toast({
              variant: 'destructive',
              title: 'Save failed',
              description: 'Failed to save human review data.',
            });
          },
        },
      );
    }
  };

  const handleSendNotification = () => {
    if (manualEmails.length > 0) {
      setConfirmSendOpen(true);
    } else {
      sendManualEmailMutation.mutate(undefined, {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: 'Notification sent',
            description: 'Manual notification email has been sent.',
          });
        },
        onError: () => {
          toast({
            variant: 'destructive',
            title: 'Send failed',
            description: 'Failed to send manual notification email.',
          });
        },
      });
    }
  };

  if (!humanReviewEnabled) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCheck className="text-muted-foreground h-5 w-5" />
            <CardTitle className="text-sm">Human Review</CardTitle>
            {humanReview && (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900 dark:text-green-300">
                Human Reviewed
              </span>
            )}
          </div>
        </div>
        <CardDescription>
          Manually provide or override outcome and{' '}
          {isLeadDataMode ? 'lead data' : 'availability data'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        {/* Outcome select */}
        <div>
          <label className="text-sm font-medium" htmlFor="hr-outcome">
            Outcome
          </label>
          <select
            id="hr-outcome"
            value={hrOutcome}
            onChange={(e) => setHrOutcome(e.target.value)}
            className="border-input bg-background mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-offset-2 focus:outline-none"
          >
            <option value="" disabled>
              -- Select outcome --
            </option>
            {outcomeOptions.map((outcome) => (
              <option key={outcome.value} value={outcome.value}>
                {outcome.label}
              </option>
            ))}
          </select>
        </div>

        {isLeadDataMode ? (
          <>
            {/* Customer Status */}
            <div>
              <label className="text-sm font-medium" htmlFor="hr-customer-status">
                Customer Status
              </label>
              <select
                id="hr-customer-status"
                value={hrCustomerStatus}
                onChange={(e) => setHrCustomerStatus(e.target.value)}
                className="border-input bg-background mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-offset-2 focus:outline-none"
              >
                <option value="">-- Select status --</option>
                <option value="still_looking">Still Looking</option>
                <option value="already_purchased">Already Purchased</option>
              </select>
            </div>

            {/* Customer Name */}
            <div>
              <label className="text-sm font-medium" htmlFor="hr-customer-name">
                Customer Name
              </label>
              <input
                id="hr-customer-name"
                type="text"
                value={hrCustomerName}
                onChange={(e) => setHrCustomerName(e.target.value)}
                placeholder="e.g. John Smith"
                className="border-input bg-background mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-offset-2 focus:outline-none"
              />
            </div>

            {/* Vehicle Interest */}
            <div>
              <label className="text-sm font-medium" htmlFor="hr-vehicle-interest">
                Vehicle Interest
              </label>
              <input
                id="hr-vehicle-interest"
                type="text"
                value={hrVehicleInterest}
                onChange={(e) => setHrVehicleInterest(e.target.value)}
                placeholder="e.g. BMW X5, SUV, family car"
                className="border-input bg-background mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-offset-2 focus:outline-none"
              />
            </div>

            {/* Budget & Finance */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium" htmlFor="hr-budget">
                  Budget
                </label>
                <input
                  id="hr-budget"
                  type="text"
                  value={hrBudget}
                  onChange={(e) => setHrBudget(e.target.value)}
                  placeholder="e.g. £500/month, around £30,000"
                  className="border-input bg-background mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-offset-2 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="hr-finance-preference">
                  Finance Preference
                </label>
                <select
                  id="hr-finance-preference"
                  value={hrFinancePreference}
                  onChange={(e) => setHrFinancePreference(e.target.value)}
                  className="border-input bg-background mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-offset-2 focus:outline-none"
                >
                  <option value="">-- Select --</option>
                  <option value="finance">Finance (monthly payments)</option>
                  <option value="outright">Outright (full payment)</option>
                  <option value="unsure">Unsure</option>
                </select>
              </div>
            </div>

            {/* Preferred Callback Time */}
            <div>
              <label className="text-sm font-medium" htmlFor="hr-callback-time">
                Preferred Callback Time
              </label>
              <input
                id="hr-callback-time"
                type="text"
                value={hrPreferredCallbackTime}
                onChange={(e) => setHrPreferredCallbackTime(e.target.value)}
                placeholder="e.g. morning, after 5pm, Tuesday afternoon"
                className="border-input bg-background mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-offset-2 focus:outline-none"
              />
            </div>

            {/* Additional Notes */}
            <div>
              <label className="text-sm font-medium" htmlFor="hr-lead-notes">
                Additional Notes
              </label>
              <textarea
                id="hr-lead-notes"
                value={hrLeadNotes}
                onChange={(e) => setHrLeadNotes(e.target.value)}
                rows={3}
                className="border-input bg-background mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-offset-2 focus:outline-none"
                placeholder="Trade-in info, specific features, urgency..."
              />
            </div>

            {/* Showroom Interest checkbox */}
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={hrShowroomInterest}
                onChange={(e) => setHrShowroomInterest(e.target.checked)}
                className="text-primary focus:ring-primary h-4 w-4 cursor-pointer rounded border-gray-300"
              />
              <span className="text-sm font-medium">Interested in visiting showroom</span>
            </label>
          </>
        ) : (
          <>
            {/* Availability Slots */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium">Availability Slots</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setHrSlots([...hrSlots, { date: '', shift_type: 'morning' }])}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Add Slot
                </Button>
              </div>
              {hrSlots.length === 0 ? (
                <p className="text-muted-foreground text-sm italic">
                  No availability slots. Click &quot;Add Slot&quot; to add one.
                </p>
              ) : (
                <div className="space-y-2">
                  {hrSlots.map((slot, idx) => (
                    <div key={idx} className="flex items-center gap-2 rounded-md border p-2">
                      <input
                        type="date"
                        value={slot.date}
                        onChange={(e) => {
                          const updated = [...hrSlots];
                          updated[idx] = { ...slot, date: e.target.value };
                          setHrSlots(updated);
                        }}
                        className="border-input bg-background rounded-md border px-3 py-1.5 text-sm"
                      />
                      <select
                        value={slot.shift_type}
                        onChange={(e) => {
                          const updated = [...hrSlots];
                          updated[idx] = { ...slot, shift_type: e.target.value };
                          setHrSlots(updated);
                        }}
                        className="border-input bg-background rounded-md border px-3 py-1.5 text-sm"
                      >
                        {SHIFT_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setHrSlots(hrSlots.filter((_, i) => i !== idx))}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {hrSlots.length > 0 && (
                    <p className="text-muted-foreground text-xs">
                      {hrSlots.length} slot{hrSlots.length !== 1 ? 's' : ''} ={' '}
                      {calculateAvailabilityHours({ availability_slots: hrSlots })} hours
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Additional Notes */}
            <div>
              <label className="text-sm font-medium" htmlFor="hr-notes">
                Additional Notes
              </label>
              <textarea
                id="hr-notes"
                value={hrNotes}
                onChange={(e) => setHrNotes(e.target.value)}
                rows={3}
                className="border-input bg-background mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-offset-2 focus:outline-none"
                placeholder="Any additional notes about availability..."
              />
            </div>

            {/* Confirmed checkbox */}
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={hrConfirmed}
                onChange={(e) => setHrConfirmed(e.target.checked)}
                className="text-primary focus:ring-primary h-4 w-4 cursor-pointer rounded border-gray-300"
              />
              <span className="text-sm font-medium">Availability confirmed</span>
            </label>

            {/* Consented checkbox */}
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={hrConsented}
                onChange={(e) => setHrConsented(e.target.checked)}
                className="text-primary focus:ring-primary h-4 w-4 cursor-pointer rounded border-gray-300"
              />
              <span className="text-sm font-medium">Consented to future calls</span>
            </label>
          </>
        )}

        {/* Save & Send buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={handleSave} disabled={upsertHumanReviewMutation.isPending}>
            <Save className="mr-2 h-4 w-4" />
            {upsertHumanReviewMutation.isPending ? 'Saving...' : 'Save Human Review'}
          </Button>
          <Button
            variant="outline"
            disabled={sendManualEmailMutation.isPending}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSendNotification();
            }}
          >
            <Mail className="mr-2 h-4 w-4" />
            {sendManualEmailMutation.isPending ? 'Sending...' : 'Send Notification'}
          </Button>
        </div>
        {humanReview?.updated_at && (
          <p className="text-muted-foreground text-xs">
            Last saved: {formatDateTime(humanReview.updated_at)}
          </p>
        )}
        {manualEmails.length > 0 && (
          <p className="text-muted-foreground text-xs">
            Manual email sent {manualEmails.length} time(s). Last sent by{' '}
            {manualEmails[0].user.name} on {new Date(manualEmails[0].sent_at).toLocaleString()}
          </p>
        )}

        {/* Confirm re-send dialog */}
        <Dialog open={confirmSendOpen} onOpenChange={setConfirmSendOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Re-send notification?</DialogTitle>
              <DialogDescription>
                Manual notification already sent {manualEmails.length} time(s). Are you sure you
                want to send again?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmSendOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setConfirmSendOpen(false);
                  sendManualEmailMutation.mutate(undefined, {
                    onSuccess: () => {
                      toast({
                        variant: 'success',
                        title: 'Notification sent',
                        description: 'Manual notification email has been sent.',
                      });
                    },
                    onError: () => {
                      toast({
                        variant: 'destructive',
                        title: 'Send failed',
                        description: 'Failed to send manual notification email.',
                      });
                    },
                  });
                }}
              >
                Send Again
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
